import { DATE_FORMATS_FNS } from "components";
import { QUESTION_RESULT_STATUSES } from "models";
import { QuestionResultStatusSelector } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import { ImageGallery, ImageLetters } from "modules/AppointmentDetails/components/Interventions/components/PinOverview/components";
import "modules/AppointmentDetails/components/Interventions/components/PinOverview/components/ChecklistSection/ChecklistSection.scss";
import TyreSection from "modules/AppointmentDetails/components/Interventions/components/PinOverview/components/ChecklistSection/components/TyreSection";
import { letterQuestionResults, useConnectedChecks } from "modules/AppointmentDetails/components/Interventions/components/PinOverview/components/ChecklistSection/hooks";
import { isInStorageTyre, isOnCarTyre, isOnTruckTyre } from "modules/AppointmentDetails/utils";
import { formatDate } from "utils/dateHelperFunctions";

import type { Intervention, Pin } from "models";
import type { LetteredCheck } from "modules/AppointmentDetails/components/Interventions/components/PinOverview/components/ChecklistSection/hooks";
import type { OrderedGroupedCheck } from "modules/AppointmentDetails/hooks";

type Props = {
  intervention?: Intervention;
  pin?: Pin;
  check?: OrderedGroupedCheck;
};

export const ChecklistSection = ({ intervention, pin, check }: Props) => {
  const { data: connectedChecks } = useConnectedChecks(intervention, pin);

  const getChecksToRender = (): LetteredCheck[] | undefined => {
    if (!check) return connectedChecks;

    return [{ ...check, question_results_with_lettered_images: letterQuestionResults(check.question_items) }];
  };

  const checksToRender = getChecksToRender();
  if (!checksToRender?.length) return null;

  return (
    <div className="ChecklistSectionContent">
      {checksToRender.map(letteredCheck => {
        const letteredImages = letteredCheck.question_results_with_lettered_images.flatMap(qr => qr.letteredImages);

        return (
          <div className="checklist-notes" key={letteredCheck.id}>
            <div className="section-header">
              {letteredCheck.checklist?.name || ""} - <span>{formatDate(letteredCheck.created_on, DATE_FORMATS_FNS.dateMonthYearTime)}</span>
              {letteredCheck.mechanic && <span> - {`${letteredCheck.mechanic.first_name} ${letteredCheck.mechanic.last_name}`.trim()}</span>}
            </div>

            <ImageGallery checkId={letteredCheck.id} letteredImages={letteredImages} />
            {letteredCheck.question_results_with_lettered_images.map(question => {
              if (question.status === QUESTION_RESULT_STATUSES.NVT) return null;

              if (question.tyre_position && (isOnCarTyre(question.tyre_position) || isOnTruckTyre(question.tyre_position) || isInStorageTyre(question.tyre_position))) {
                return <TyreSection key={question.id} question={question} />;
              }

              return (
                <div className="checklist-item" key={question.id}>
                  <div>
                    <div className="checklist-item-text-image-letter">
                      <ImageLetters letteredImages={question.letteredImages} />
                      <div className="checklist-item-title">{question.title}</div>
                    </div>
                    <p className="checklist-item-description">{question.raw}</p>
                  </div>
                  <QuestionResultStatusSelector questionResult={question} disabled={true} />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
